import { toast } from "@assetwatch/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { workClient } from "../clients/workClient";
import { useWorkScope } from "../components/WorkScopeProvider";

function errorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

export async function getResponseErrorMessage(
  response: { json: () => Promise<unknown> },
  fallback: string,
) {
  try {
    const body = await response.json();
    if (
      typeof body === "object" &&
      body !== null &&
      "error" in body &&
      typeof body.error === "string"
    ) {
      return body.error;
    }
  } catch {
    // Use the operation-specific fallback when the response isn't valid JSON.
  }
  return fallback;
}

export function useSettingsCategoryMutations() {
  const queryClient = useQueryClient();
  const { selectedCustomer, selectedFacility } = useWorkScope();

  function mutationContext() {
    if (!selectedCustomer || !selectedFacility) {
      throw new Error("Select a site before managing categories.");
    }
    return {
      customerId: selectedCustomer.id,
      facilityId: selectedFacility.id,
    };
  }

  async function invalidateCategories() {
    const { customerId, facilityId } = mutationContext();
    await queryClient.invalidateQueries({
      queryKey: ["settings-categories", customerId, facilityId],
    });
  }

  const create = useMutation({
    mutationFn: async (name: string) => {
      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ].settings.categories.$post({
        param: mutationContext(),
        json: { name },
      });
      if (!response.ok) {
        throw new Error(
          await getResponseErrorMessage(response, "Unable to add category."),
        );
      }
      return response.json();
    },
    onSuccess: async () => {
      toast.add("Category added.", { icon: true });
      await invalidateCategories();
    },
    onError: (error) =>
      toast.add(errorMessage(error, "Unable to add category.")),
  });

  const update = useMutation({
    mutationFn: async ({
      categoryId,
      name,
    }: {
      categoryId: number;
      name: string;
    }) => {
      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ].settings.categories[":categoryId"].$patch({
        param: { ...mutationContext(), categoryId: String(categoryId) },
        json: { name },
      });
      if (!response.ok) {
        throw new Error(
          await getResponseErrorMessage(response, "Unable to rename category."),
        );
      }
      return response.json();
    },
    onSuccess: async () => {
      toast.add("Category updated.", { icon: true });
      await invalidateCategories();
    },
    onError: (error) =>
      toast.add(errorMessage(error, "Unable to rename category.")),
  });

  const remove = useMutation({
    mutationFn: async (categoryId: number) => {
      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ].settings.categories[":categoryId"].$delete({
        param: { ...mutationContext(), categoryId: String(categoryId) },
      });
      if (!response.ok) {
        throw new Error(
          await getResponseErrorMessage(response, "Unable to delete category."),
        );
      }
      return response.json();
    },
    onSuccess: async () => {
      toast.add("Category deleted.", { icon: true });
      await invalidateCategories();
    },
    onError: (error) =>
      toast.add(errorMessage(error, "Unable to delete category.")),
  });

  return { create, update, remove };
}
